import React, { useEffect, useState } from "react"
import * as moment from "moment"
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Form,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap"
import BootstrapTable from "react-bootstrap-table-next"
import TableLoader from "components/Common/TableLoader"
import {
  getAffiliateCommissionRulesCall,
  createAffiliateCommissionRuleCall,
  updateAffiliateCommissionRuleCall,
  deleteAffiliateCommissionRuleCall,
} from "helpers/repository"
import { showErrorMessages } from "helpers/helpers"
import { showMessage } from "components/Common/Toaster"

const EMPTY_FORM = {
  commission_model: "REV_SHARE",
  rev_share_percentage: "",
  cpa_amount: "",
  fixed_amount: "",
  fixed_frequency: "",
  fixed_description: "",
  currency: "NGN",
  effective_from: "",
  effective_to: "",
  status: "active",
}

const MODEL_OPTIONS = [
  { value: "REV_SHARE", label: "Revenue Share", color: "#556ee6" },
  { value: "CPA", label: "CPA", color: "#343a40" },
  { value: "FIXED", label: "Fixed", color: "#f46a6a" },
  { value: "HYBRID", label: "Hybrid (Rev Share + CPA)", color: "#7367f0" },
]

const formatMoney = value =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })

export default function AffiliateCommissionRulesModal({
  isOpen,
  setIsOpen,
  affiliate,
}) {
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState("list") // list | form
  const [editingRule, setEditingRule] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [deletingRule, setDeletingRule] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const canCreate = true
  const canUpdate = true
  const canDelete = true

  const fetchRules = async () => {
    if (!affiliate?.id) return
    setLoading(true)
    try {
      const response = await getAffiliateCommissionRulesCall(affiliate.id)
      setRules(response?.data?.data || [])
    } catch (error) {
      showErrorMessages(error)
      setRules([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      setView("list")
      fetchRules()
    }
  }, [isOpen, affiliate?.id])

  const toggle = () => setIsOpen(false)

  const openCreateForm = () => {
    setEditingRule(null)
    setForm(EMPTY_FORM)
    setView("form")
  }

  const openEditForm = rule => {
    setEditingRule(rule)
    setForm({
      commission_model: rule.commission_model || "REV_SHARE",
      rev_share_percentage: rule.rev_share_percentage ?? "",
      cpa_amount: rule.cpa_amount ?? "",
      fixed_amount: rule.fixed_amount ?? "",
      fixed_frequency: rule.fixed_frequency ?? "",
      fixed_description: rule.fixed_description ?? "",
      currency: rule.currency || "NGN",
      effective_from: rule.effective_from || "",
      effective_to: rule.effective_to || "",
      status: rule.status || "active",
    })
    setView("form")
  }

  const buildPayload = () => {
    const payload = {
      commission_model: form.commission_model,
      currency: form.currency,
      effective_from: form.effective_from,
      effective_to: form.effective_to || null,
    }

    if (["REV_SHARE", "HYBRID"].includes(form.commission_model)) {
      payload.rev_share_percentage = Number(form.rev_share_percentage)
    }
    if (["CPA", "HYBRID"].includes(form.commission_model)) {
      payload.cpa_amount = Number(form.cpa_amount)
    }
    if (form.commission_model === "FIXED") {
      payload.fixed_amount = Number(form.fixed_amount)
      payload.fixed_frequency = form.fixed_frequency
      payload.fixed_description = form.fixed_description
    }
    if (editingRule) {
      payload.status = form.status
    }

    return payload
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = buildPayload()
      if (editingRule) {
        const response = await updateAffiliateCommissionRuleCall(
          affiliate.id,
          editingRule.id,
          payload
        )
        showMessage("success", response?.data?.message)
      } else {
        const response = await createAffiliateCommissionRuleCall(
          affiliate.id,
          payload
        )
        showMessage("success", response?.data?.message)
      }
      await fetchRules()
      setView("list")
    } catch (error) {
      showErrorMessages(error)
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!deletingRule) return
    setDeleting(true)
    try {
      const response = await deleteAffiliateCommissionRuleCall(
        affiliate.id,
        deletingRule.id
      )
      showMessage("success", response?.data?.message)
      setDeletingRule(null)
      await fetchRules()
    } catch (error) {
      showErrorMessages(error)
    } finally {
      setDeleting(false)
    }
  }

  const columns = [
    { dataField: "commission_model", text: "Model" },
    {
      dataField: "rev_share_percentage",
      text: "Rev Share %",
      formatter: cell => (cell != null ? `${cell}%` : "—"),
    },
    {
      dataField: "cpa_amount",
      text: "CPA Amount",
      formatter: cell => (cell != null ? formatMoney(cell) : "—"),
    },
    {
      dataField: "fixed_amount",
      text: "Fixed Amount",
      formatter: cell => (cell != null ? formatMoney(cell) : "—"),
    },
    { dataField: "currency", text: "Currency" },
    {
      dataField: "effective_from",
      text: "Effective From",
      formatter: cell => (cell ? moment(cell).format("DD/MM/YYYY") : "—"),
    },
    {
      dataField: "effective_to",
      text: "Effective To",
      formatter: cell => (cell ? moment(cell).format("DD/MM/YYYY") : "—"),
    },
    {
      dataField: "status",
      text: "Status",
      formatter: cell => (
        <span
          className={`badge badge-soft-${
            cell?.toLowerCase() === "active" ? "success" : "secondary"
          } font-size-12`}
        >
          {cell}
        </span>
      ),
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (_cell, row) => (
        <div className="d-flex gap-2">
          {canUpdate && (
            <Button
              size="sm"
              color="primary"
              outline
              onClick={() => openEditForm(row)}
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm"
              color="danger"
              outline
              onClick={() => setDeletingRule(row)}
            >
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ]

  const auditColumns = [
    {
      dataField: "created_at",
      text: "Date",
      formatter: cell => (cell ? moment(cell).format("DD/MM/YYYY HH:mm") : "—"),
    },
    { dataField: "action", text: "Action" },
    {
      dataField: "user",
      text: "User",
      formatter: (cell, row) => row?.user?.username || row?.user_id || "—",
    },
    {
      dataField: "new_values",
      text: "Changes",
      formatter: (cell, row) => (
        <pre
          className="mb-0"
          style={{ fontSize: "11px", whiteSpace: "pre-wrap" }}
        >
          {JSON.stringify(
            { previous: row.previous_values, new: row.new_values },
            null,
            2
          )}
        </pre>
      ),
    },
  ]

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
      <ModalHeader toggle={toggle}>
        Commission Rules — {affiliate?.username || affiliate?.referral_code}
      </ModalHeader>
      <ModalBody>
        {view === "list" && (
          <>
            <div className="d-flex justify-content-end gap-2 mb-3">
              {canCreate && (
                <Button color="success" size="sm" onClick={openCreateForm}>
                  Add Rule
                </Button>
              )}
            </div>
            {loading ? (
              <TableLoader />
            ) : !rules || rules.length === 0 ? (
              <div className="text-center py-5">
                <i
                  className="bx bx-money"
                  style={{ fontSize: "40px", color: "#c3cbe4" }}
                />
                <h5 className="mt-3 mb-1">No commission rules found</h5>
                <p className="text-muted mb-0">
                  Add a rule to start tracking this affiliate&apos;s commission.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <BootstrapTable
                  keyField="id"
                  responsive
                  bordered={false}
                  striped={false}
                  classes="table align-middle table-nowrap"
                  headerWrapperClasses="table-light"
                  columns={columns}
                  data={rules}
                />
              </div>
            )}
          </>
        )}

        {view === "form" && (
          <Form onSubmit={handleSubmit}>
            <div className="mb-3">
              <Label>Commission Model</Label>
              <Row>
                {MODEL_OPTIONS.map(option => {
                  const isSelected = form.commission_model === option.value
                  return (
                    <Col md={6} className="mb-2" key={option.value}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          setForm({ ...form, commission_model: option.value })
                        }
                        onKeyDown={e => {
                          if (e.key === "Enter" || e.key === " ") {
                            setForm({ ...form, commission_model: option.value })
                          }
                        }}
                        className="p-3 rounded"
                        style={{
                          border: `1px solid ${
                            isSelected ? option.color : "#ced4da"
                          }`,
                          backgroundColor: isSelected
                            ? `${option.color}15`
                            : "transparent",
                          color: option.color,
                          fontWeight: isSelected ? 600 : 500,
                          cursor: "pointer",
                        }}
                      >
                        {option.label}
                      </div>
                    </Col>
                  )
                })}
              </Row>
            </div>

            <Row>
              <Col md={6} className="mb-3">
                <Label>Currency</Label>
                <Input
                  type="select"
                  value={form.currency}
                  onChange={e => setForm({ ...form, currency: e.target.value })}
                  required
                >
                  <option value="NGN">NGN</option>
                  <option value="GHS">GHS</option>
                  <option value="USD">USD</option>
                </Input>
              </Col>

              {["REV_SHARE", "HYBRID"].includes(form.commission_model) && (
                <Col md={6} className="mb-3">
                  <Label>Rev Share Percentage</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="e.g. 20%"
                    value={form.rev_share_percentage}
                    onChange={e =>
                      setForm({ ...form, rev_share_percentage: e.target.value })
                    }
                    required
                  />
                </Col>
              )}

              {form.commission_model === "CPA" && (
                <Col md={6} className="mb-3">
                  <Label>CPA Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.cpa_amount}
                    onChange={e =>
                      setForm({ ...form, cpa_amount: e.target.value })
                    }
                    required
                  />
                </Col>
              )}

              {form.commission_model === "FIXED" && (
                <Col md={6} className="mb-3">
                  <Label>Fixed Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.fixed_amount}
                    onChange={e =>
                      setForm({ ...form, fixed_amount: e.target.value })
                    }
                    required
                  />
                </Col>
              )}
            </Row>

            {form.commission_model === "HYBRID" && (
              <Row>
                <Col md={6} className="mb-3">
                  <Label>CPA Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.cpa_amount}
                    onChange={e =>
                      setForm({ ...form, cpa_amount: e.target.value })
                    }
                    required
                  />
                </Col>
              </Row>
            )}

            {form.commission_model === "FIXED" && (
              <Row>
                <Col md={6} className="mb-3">
                  <Label>Fixed Frequency</Label>
                  <Input
                    type="select"
                    value={form.fixed_frequency}
                    onChange={e =>
                      setForm({ ...form, fixed_frequency: e.target.value })
                    }
                    required
                  >
                    <option value="">Select...</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </Input>
                </Col>
                <Col md={6} className="mb-3">
                  <Label>Description</Label>
                  <Input
                    type="text"
                    value={form.fixed_description}
                    onChange={e =>
                      setForm({ ...form, fixed_description: e.target.value })
                    }
                  />
                </Col>
              </Row>
            )}

            <Row>
              <Col md={4} className="mb-3">
                <Label>Effective From</Label>
                <Input
                  type="date"
                  value={form.effective_from}
                  onChange={e =>
                    setForm({ ...form, effective_from: e.target.value })
                  }
                  required
                />
              </Col>
              <Col md={4} className="mb-3">
                <Label>Effective To</Label>
                <Input
                  type="date"
                  value={form.effective_to}
                  onChange={e =>
                    setForm({ ...form, effective_to: e.target.value })
                  }
                />
              </Col>
              {editingRule && (
                <Col md={4} className="mb-3">
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Input>
                </Col>
              )}
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button
                type="button"
                color="secondary"
                outline
                onClick={() => setView("list")}
              >
                Cancel
              </Button>
              <Button type="submit" color="success" disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </Form>
        )}

        {view === "audit" && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Audit Log</h6>
              <Button
                size="sm"
                color="secondary"
                outline
                onClick={() => setView("list")}
              >
                Back to Rules
              </Button>
            </div>
          </>
        )}
      </ModalBody>

      {deletingRule && (
        <Modal isOpen centered toggle={() => setDeletingRule(null)}>
          <ModalHeader toggle={() => setDeletingRule(null)}>
            Delete Commission Rule
          </ModalHeader>
          <ModalBody>
            <p>
              Are you sure you want to delete this{" "}
              {deletingRule.commission_model} commission rule? This action
              cannot be undone.
            </p>
            <div className="d-flex justify-content-end gap-2">
              <Button
                color="secondary"
                outline
                onClick={() => setDeletingRule(null)}
              >
                Cancel
              </Button>
              <Button
                color="danger"
                disabled={deleting}
                onClick={confirmDelete}
              >
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </Modal>
  )
}
